import { Routes, Route, Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { PasswordGate } from './auth/PasswordGate';
import { Layout } from './components/Layout';
import { PlaidItemsProvider } from './data/usePlaidItems';
import { ScopeProvider } from './hooks/useScope';
import { DashboardPage } from './pages/DashboardPage';
import { HomePage } from './pages/home/HomePage';
import { NetWorthPage } from './pages/NetWorthPage';
import { IncomeHistoryPage } from './pages/IncomeHistoryPage';
import { BudgetPage } from './pages/BudgetPage';
import { LinksPage } from './pages/LinksPage';
import { RetirementPage } from './pages/RetirementPage';
import { RulesPage } from './pages/RulesPage';
import { SettingsPage } from './pages/SettingsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { KioskShell } from './pages/kiosk/KioskShell';

function GatedOutlet() {
  return (
    <PasswordGate>
      <PlaidItemsProvider>
        <ScopeProvider>
          <Outlet />
        </ScopeProvider>
      </PlaidItemsProvider>
    </PasswordGate>
  );
}

// Legacy /transactions URLs (and links like /transactions?account=X) now
// resolve to the Transactions tab inside /spending, preserving any other
// query params the original link carried.
function LegacyTransactionsRedirect() {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set('tab', 'transactions');
  return <Navigate to={`/spending?${next.toString()}`} replace />;
}

// /gifts now lives as the Gifts tab inside /spending.
function LegacyGiftsRedirect() {
  return <Navigate to="/spending?tab=gifts" replace />;
}

// /bills now lives as the Bills tab inside /spending.
function LegacyBillsRedirect() {
  return <Navigate to="/spending?tab=bills" replace />;
}

// /budget renamed to /spending — preserve query params so bookmarks like
// /budget?tab=gifts or /budget?cat=Home still land on the right view.
function LegacyBudgetRedirect() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={qs ? `/spending?${qs}` : '/spending'} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Public — Plaid and end users must reach the privacy policy without signing in. */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />

      {/* Kiosk SPA. Outside the human PasswordGate by design: the kiosk's
          credential is a long-lived device token in localStorage, not an
          interactive password. The route's HTML bundle is public but every
          /api/* call still requires the bearer — without a token in
          localStorage the user sees only the enrollment screen. */}
      <Route path="/kiosk/*" element={<KioskShell />} />

      <Route element={<GatedOutlet />}>
        {/* The family home screen — the app's front door. It has its own
            minimal chrome, so it sits outside the finance Layout. */}
        <Route path="/" element={<HomePage />} />
        <Route element={<Layout />}>
          <Route path="/finance" element={<DashboardPage />} />
          <Route path="/net-worth" element={<NetWorthPage />} />
          <Route path="/income" element={<IncomeHistoryPage />} />
          <Route path="/spending" element={<BudgetPage />} />
          <Route path="/budget" element={<LegacyBudgetRedirect />} />
          <Route path="/bills" element={<LegacyBillsRedirect />} />
          <Route path="/gifts" element={<LegacyGiftsRedirect />} />
          <Route path="/transactions" element={<LegacyTransactionsRedirect />} />
          <Route path="/retirement" element={<RetirementPage />} />
          <Route path="/links" element={<LinksPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/rules" element={<RulesPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
